import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useFeatureFlag } from '../useFeatureFlag/useFeatureFlag';
import { useFeatureFlagsLoader } from '../hooks/useFeatureFlagsLoader';
import { FeatureFlagUseCase, AppDomain } from '@ibk/shared-types';
import { config } from '@ibk/shared-config';
const DEBUG_STYLES = {
    container: {
        position: 'fixed',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '16px',
        fontFamily: 'monospace',
        fontSize: '12px',
        zIndex: 9999,
        maxWidth: '400px',
        maxHeight: '600px',
        overflow: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid #333',
    },
    title: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#60a5fa',
    },
    button: {
        backgroundColor: '#374151',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: '10px',
        marginLeft: '4px',
    },
    section: {
        marginBottom: '12px',
        padding: '8px',
        backgroundColor: '#2d2d2d',
        borderRadius: '4px',
    },
    sectionTitle: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#fbbf24',
        marginBottom: '4px',
    },
    input: {
        backgroundColor: '#374151',
        color: '#fff',
        border: '1px solid #4b5563',
        borderRadius: '4px',
        padding: '4px 8px',
        fontSize: '11px',
        width: '100%',
        marginBottom: '4px',
    },
    status: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        marginBottom: '4px',
    },
    success: {
        backgroundColor: '#065f46',
        color: '#10b981',
    },
    error: {
        backgroundColor: '#7f1d1d',
        color: '#ef4444',
    },
    warning: {
        backgroundColor: '#92400e',
        color: '#f59e0b',
    },
    info: {
        backgroundColor: '#1e3a8a',
        color: '#3b82f6',
    },
};
export const DebugFeatureFlags = ({ userId: propUserId, position = 'bottom-right', minimized: propMinimized = false, }) => {
    const [testUserId, setTestUserId] = useState(propUserId || '');
    const [minimized, setMinimized] = useState(propMinimized);
    const [selectedUseCase, setSelectedUseCase] = useState(FeatureFlagUseCase.NEW_IBKS_ACCESS_CONTROL);
    const [selectedDomain, setSelectedDomain] = useState(AppDomain.EMPRESA);
    const featureFlagHook = useFeatureFlag(testUserId);
    const { isFeatureFlagWhiteListed, isFeatureFlagBlackListed, getFlagPerAppName, getFeatureFlags, isLoading, error, refetch, } = featureFlagHook || {};
    const loaderHook = useFeatureFlagsLoader({
        userId: testUserId,
        cacheEnabled: true,
        backgroundRefresh: false,
        staleWhileRevalidate: false,
    });
    const { data: loaderData, isLoading: loaderIsLoading, error: loaderError, isStale, reload, clearCache, } = loaderHook || {};
    const debugMode = config.featureFlagsDebugMode === 'true';
    if (!debugMode) {
        return null;
    }
    const positionStyles = {
        'bottom-right': { bottom: '16px', right: '16px' },
        'bottom-left': { bottom: '16px', left: '16px' },
        'top-right': { top: '16px', right: '16px' },
        'top-left': { top: '16px', left: '16px' },
    };
    const containerStyle = {
        ...DEBUG_STYLES.container,
        ...positionStyles[position],
    };
    if (minimized) {
        return (_jsx("div", { style: containerStyle, children: _jsxs("div", { style: DEBUG_STYLES.header, children: [_jsx("span", { style: DEBUG_STYLES.title, children: "Feature Flags Debug" }), _jsx("button", { style: DEBUG_STYLES.button, onClick: () => setMinimized(false), children: "Expand" })] }) }));
    }
    const currentUserId = testUserId || propUserId || 'no-user';
    const isWhitelisted = isFeatureFlagWhiteListed?.(selectedUseCase, currentUserId) || false;
    const isBlacklisted = isFeatureFlagBlackListed?.(selectedUseCase, currentUserId) || false;
    const domainFlag = getFlagPerAppName?.(selectedDomain, selectedUseCase) || null;
    return (_jsxs("div", { style: containerStyle, children: [_jsxs("div", { style: DEBUG_STYLES.header, children: [_jsx("span", { style: DEBUG_STYLES.title, children: "Feature Flags Debug" }), _jsxs("div", { children: [_jsx("button", { style: DEBUG_STYLES.button, onClick: () => setMinimized(true), children: "Minimize" }), _jsx("button", { style: DEBUG_STYLES.button, onClick: refetch || (() => { }), children: "Refetch" })] })] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Status" }), _jsx("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...(isLoading || loaderIsLoading ? DEBUG_STYLES.warning : DEBUG_STYLES.success),
                        }, children: isLoading || loaderIsLoading ? 'Loading...' : 'Ready' }), (error ?? loaderError) && (_jsxs("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...DEBUG_STYLES.error,
                        }, children: ["Error: ", error ?? loaderError] })), isStale && (_jsx("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...DEBUG_STYLES.warning,
                        }, children: "Data is stale" }))] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Test User ID" }), _jsx("input", { style: DEBUG_STYLES.input, value: testUserId, onChange: (e) => setTestUserId(e.target.value), placeholder: "Enter user ID to test" })] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Use Case" }), _jsx("select", { style: DEBUG_STYLES.input, value: selectedUseCase, onChange: (e) => setSelectedUseCase(e.target.value), children: Object.values(FeatureFlagUseCase).map((useCase) => (_jsx("option", { value: useCase, children: useCase }, useCase))) })] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Domain" }), _jsx("select", { style: DEBUG_STYLES.input, value: selectedDomain, onChange: (e) => setSelectedDomain(e.target.value), children: Object.values(AppDomain).map((domain) => (_jsx("option", { value: domain, children: domain }, domain))) })] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Test Results" }), _jsxs("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...(isWhitelisted ? DEBUG_STYLES.success : DEBUG_STYLES.error),
                        }, children: ["Whitelisted: ", isWhitelisted ? 'YES' : 'NO'] }), _jsxs("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...(isBlacklisted ? DEBUG_STYLES.error : DEBUG_STYLES.success),
                        }, children: ["Blacklisted: ", isBlacklisted ? 'YES' : 'NO'] }), _jsxs("div", { style: {
                            ...DEBUG_STYLES.status,
                            ...(domainFlag ? DEBUG_STYLES.info : DEBUG_STYLES.warning),
                        }, children: ["Domain Flag: ", domainFlag ? 'Found' : 'Not Found'] })] }), domainFlag && (_jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Domain Flag Details" }), _jsxs("div", { style: { fontSize: '10px', color: '#9ca3af' }, children: [_jsxs("div", { children: ["Use Case: ", domainFlag.useCase] }), _jsxs("div", { children: ["State: ", domainFlag.state] }), _jsxs("div", { children: ["Enabled: ", domainFlag.enabled ? 'YES' : 'NO'] }), _jsxs("div", { children: ["Domain: ", domainFlag.domain || 'N/A'] }), domainFlag.whitelistedUsers && (_jsxs("div", { children: ["Whitelist: ", domainFlag.whitelistedUsers.join(', ')] })), domainFlag.blacklistedUsers && (_jsxs("div", { children: ["Blacklist: ", domainFlag.blacklistedUsers.join(', ')] }))] })] })), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Raw Data" }), _jsxs("div", { style: { fontSize: '10px', color: '#9ca3af' }, children: [_jsxs("div", { children: ["Total Flags: ", loaderData?.featureFlags?.length ?? 0] }), _jsxs("div", { children: ["Version: ", loaderData?.version ?? 'N/A'] }), _jsxs("div", { children: ["Timestamp: ", loaderData?.timestamp ?? 'N/A'] })] })] }), _jsxs("div", { style: DEBUG_STYLES.section, children: [_jsx("div", { style: DEBUG_STYLES.sectionTitle, children: "Actions" }), _jsx("button", { style: DEBUG_STYLES.button, onClick: reload || (() => { }), children: "Reload" }), _jsx("button", { style: DEBUG_STYLES.button, onClick: clearCache || (() => { }), children: "Clear Cache" }), _jsx("button", { style: DEBUG_STYLES.button, onClick: () => getFeatureFlags?.(), children: "Get Flags" })] })] }));
};
